import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import {
  ACCOUNT_KEY,
  DB_NAME,
  DB_VERSION,
  SCHEMA_VERSION,
  STARTING_EQUITY,
} from './config';
import type { AccountState, SessionMeta, StatsFilter, Trade } from './types';

interface InvestmentTeachDB extends DBSchema {
  trades: {
    key: string;
    value: Trade;
    indexes: {
      'by-chartId': string;
      'by-setup': string;
      'by-enteredAt': number;
      'by-resolution': string;
      'by-direction': string;
    };
  };
  account: {
    key: string;
    value: AccountState & { id: string };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown };
  };
}

let dbPromise: Promise<IDBPDatabase<InvestmentTeachDB>> | null = null;

function getDB(): Promise<IDBPDatabase<InvestmentTeachDB>> {
  if (!dbPromise) {
    dbPromise = openDB<InvestmentTeachDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('trades')) {
          const trades = db.createObjectStore('trades', { keyPath: 'id' });
          trades.createIndex('by-chartId', 'chartId');
          trades.createIndex('by-setup', 'plan.setup' as never);
          trades.createIndex('by-enteredAt', 'enteredAt');
          trades.createIndex('by-resolution', 'resolution');
          trades.createIndex('by-direction', 'plan.direction' as never);
        }
        if (!db.objectStoreNames.contains('account')) {
          db.createObjectStore('account', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    }).then(async (db) => {
      await db.put('meta', { key: 'schemaVersion', value: SCHEMA_VERSION });
      return db;
    });
  }
  return dbPromise;
}

export async function saveTrade(trade: Trade): Promise<void> {
  const db = await getDB();
  await db.put('trades', trade);
}

export async function updateTrade(trade: Trade): Promise<void> {
  const db = await getDB();
  await db.put('trades', trade);
}

export async function listTrades(filter?: StatsFilter): Promise<Trade[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('trades', 'by-enteredAt');
  if (!filter) return all;
  return all.filter((t) => {
    if (filter.setup !== undefined && t.plan.setup !== filter.setup) return false;
    if (filter.direction !== undefined && t.plan.direction !== filter.direction) return false;
    if (filter.yFlipped !== undefined && t.yFlipped !== filter.yFlipped) return false;
    if (filter.from !== undefined && t.enteredAt < filter.from) return false;
    if (filter.to !== undefined && t.enteredAt > filter.to) return false;
    return true;
  });
}

const STARTER: AccountState = {
  equity: STARTING_EQUITY,
  peakEquity: STARTING_EQUITY,
  currentDrawdown: 0,
};

export async function getAccount(): Promise<AccountState> {
  const db = await getDB();
  const rec = await db.get('account', ACCOUNT_KEY);
  if (!rec) return { ...STARTER };
  return { equity: rec.equity, peakEquity: rec.peakEquity, currentDrawdown: rec.currentDrawdown };
}

export async function saveAccount(state: AccountState): Promise<void> {
  const db = await getDB();
  await db.put('account', { id: ACCOUNT_KEY, ...state });
}

export async function resetAccount(): Promise<void> {
  const db = await getDB();
  await db.put('account', { id: ACCOUNT_KEY, ...STARTER });
}

const SESSION_META_KEY = 'currentSession';

export async function getSessionMeta(): Promise<SessionMeta | null> {
  const db = await getDB();
  const rec = await db.get('meta', SESSION_META_KEY);
  return rec ? (rec.value as SessionMeta) : null;
}

export async function setSessionMeta(meta: SessionMeta): Promise<void> {
  const db = await getDB();
  await db.put('meta', { key: SESSION_META_KEY, value: meta });
}

export async function clearSessionMeta(): Promise<void> {
  const db = await getDB();
  await db.delete('meta', SESSION_META_KEY);
}

/**
 * Test-only: closes the cached connection and deletes the database so the next
 * call rebuilds the schema. Not exported via window.tradingEngine.
 */
export async function resetDatabaseForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}
